import React, { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';

import { TableHeader } from 'components/TableHeader';
import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { useDrawers } from 'hooks/useDrawers';
import { ServicesService } from 'services/ServicesService';
import {
  REQUEST_LOADING,
  SUCCESS_CREATE,
  SUCCESS_DELETE,
  SUCCESS_UPDATE,
} from 'constants/alerts';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { usePagination } from 'hooks/usePagination';

import { NewServicesTable } from './NewServicesTable';
import { NewServicesForm } from './NewServicesForm';

function NewServices() {
  useDocumentTitle('Сервисы');
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const { setCount } = useHeaderChanger('Сервисы');
  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();
  const { page, perPage, totalRows, setPagination, setTotalRows } =
    usePagination();

  const fetchServices = useCallback(async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await ServicesService.getNewServices({
        page,
        perPage,
      });
      setServices(items);
      setTotalRows(total);
      setCount(total);
    } catch (err) {
      message.error('Не удалось загрузить сервисы');
    } finally {
      setLoading(false);
    }
  }, [page, perPage]);

  useEffect(() => fetchServices(), [fetchServices]);

  const handleCreate = async (data) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const newService = await ServicesService.createNewService(data);
      setServices([...services, newService]);
      closeForm();
      message.success(SUCCESS_CREATE);
    } catch (err) {
      message.error('Не удалось создать сервис');
    } finally {
      hide();
    }
  };

  const handleUpdate = async (data, id) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await ServicesService.updateService(data, id);

      if (isUpdated) {
        closeForm();
        fetchServices();
        message.success(SUCCESS_UPDATE);
      }
    } catch (err) {
      message.error('Не удалось отредактировать сервис');
    } finally {
      hide();
    }
  };

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await ServicesService.deleteNewService(activeRecord.id);

      if (isDeleted) {
        setServices(services.filter((item) => item.id !== activeRecord.id));
        closeDrawer();
        message.success(SUCCESS_DELETE);
      }
    } catch (err) {
      message.error('Не удалось удалить сервис');
    } finally {
      hide();
    }
  };

  return (
    <>
      <TableHeader onCreate={openCreateForm} pageId="service" />

      <NewServicesTable
        items={services}
        loading={loading}
        current={page}
        pageSize={perPage}
        onRowClick={openDrawer}
        total={totalRows}
        onChange={setPagination}
      />

      <NewServicesForm
        isActive={formIsActive}
        initialValues={activeRecord}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onClose={closeForm}
      />

      <RecordInfoDrawer
        record={activeRecord}
        pageId="service"
        recordFields={[
          { label: 'Наименование', key: 'title' },
          { label: 'Наименование (каз)', key: 'title_kz' },
          { label: 'Описание', key: 'description' },
          { label: 'Описание (каз)', key: 'description_kz' },
          {
            label: 'Доступен',
            key: 'isPublic',
            computedValue: activeRecord?.isPublic ? 'Да' : 'Нет',
          },
        ]}
        drawerTitle={activeRecord?.title}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
        deleteConfirmText="Вы точно хотите удалить этот сервис?"
      />
    </>
  );
}

export { NewServices };
